import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolSpikesData, loadProtocolSpikeCountsData, loadProtocolRMSSNRData, loadProtocolDAQStepBounds} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "@/common/types";
import {Box, Loader, Select, NumberInput, Text, Button, RangeSlider} from "@mantine/core";

interface SpikesChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

const DEFAULT_LIMIT = 100;

export const SpikesChart: React.FC<SpikesChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [spikesData, setSpikesData] = useState<PlotParams | undefined>()
    const [loading, setLoading] = useState<boolean>(false)
    const [channelOptions, setChannelOptions] = useState<Array<{value:string; label:string}>>([])
    // canonical channel order derived from RMSSNR data
    const [channelOrder, setChannelOrder] = useState<string[]>([])
    const [channelOrderToUse, setChannelOrderToUse] = useState<string[]>([])
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null)
    const [thresholdMultiplier, setThresholdMultiplier] = useState<number>(-4.0)
    const [limit, setLimit] = useState<number>(DEFAULT_LIMIT)
    const [page, setPage] = useState<number>(1)
    const [totalSpikes, setTotalSpikes] = useState<number>(0)
    const [countsMap, setCountsMap] = useState<Map<string, number>>(new Map())

    // applied* states are the committed values used to actually load charts.
    const [appliedThreshold, setAppliedThreshold] = useState<number>(thresholdMultiplier)
    const [appliedLimit, setAppliedLimit] = useState<number>(limit)
    const [appliedChannel, setAppliedChannel] = useState<string | null>(selectedChannel)

    // session time bounds and user-editable time window
    const [sessionStart, setSessionStart] = useState<string | null>(null) // ISO
    const [sessionEnd, setSessionEnd] = useState<string | null>(null) // ISO
    // startOffset: minutes after sessionStart (start of window)
    const [startOffset, setStartOffset] = useState<number>(0)
    // endOffset: minutes after sessionStart (end of window)
    const [endOffset, setEndOffset] = useState<number>(0)
    // window size (seconds)
    const [windowSizeSeconds, setWindowSizeSeconds] = useState<number>(120)
    const [appliedTimeStart, setAppliedTimeStart] = useState<string | null>("unset")
    const [appliedTimeEnd, setAppliedTimeEnd] = useState<string | null>("unset")
    const [appliedStartOffset, setAppliedStartOffset] = useState<number | null>(null)
    const [appliedEndOffset, setAppliedEndOffset] = useState<number | null>(null)
    const [sessionTotalSeconds, setSessionTotalSeconds] = useState<number>(0)

    // UI state for dismissable time-missing warning
    // track whether the user dismissed the time-missing warning; reset when protocol changes
    const [timeWarningDismissed, setTimeWarningDismissed] = useState<boolean>(false)

    // reset dismissal when protocol changes so the user sees warning for new protocol
    useEffect(() => {
        setTimeWarningDismissed(false)
    }, [selectedProtocol])

    // if times become null (e.g., were available then became missing), ensure the warning can reappear
    useEffect(() => {
        if (appliedTimeStart == null || appliedTimeEnd == null) {
            setTimeWarningDismissed(false)
        }
    }, [appliedTimeStart, appliedTimeEnd])

    // auto-refresh tick increments every minute to trigger data reloads
    const [autoRefreshTick, setAutoRefreshTick] = useState<number>(0)
    useEffect(() => {
        if (!selectedProtocol) return
        const id = window.setInterval(() => {
            setAutoRefreshTick((t) => t + 1)
        }, 60_000) // 60 seconds
        return () => window.clearInterval(id)
    }, [selectedProtocol])

    // helper to compute ISO UTC string from milliseconds
    const isoFromMs = (ms: number | null) => (ms == null ? null : new Date(ms).toISOString())

    // load session start and end times, and update on refresh
    useEffect(() => {
        if (!selectedProtocol) return
        setLoading(true)
        loadProtocolDAQStepBounds(activeCompany.id, selectedProtocol).then((data) => {
            const startIso = data.start
            const endIso = data.end
            setSessionStart(startIso)
            setSessionEnd(endIso)

            if (startIso && endIso) {
                const startMs = new Date(startIso).getTime()
                const endMs = new Date(endIso).getTime() + 1 // add 1ms to include any truncated microseconds
                const totalSeconds = Math.max(0, (endMs - startMs) / 1000)
                setSessionTotalSeconds(totalSeconds)

                // defaults: last 2 minutes
                const defaultStartOffset = Math.max(0, totalSeconds - 120)
                const defaultEndOffset = totalSeconds
                const defaultWindowSize = Math.min(120, totalSeconds)
                // applied times: windowStart..windowEnd
                const windowStart = new Date(startMs + defaultStartOffset * 1000)
                const windowEnd = new Date(endMs)

                // fall back to defaults
                if (appliedStartOffset == null || appliedEndOffset == null || appliedTimeStart == "unset" || appliedTimeEnd == "unset") {
                    setStartOffset(defaultStartOffset)
                    setEndOffset(defaultEndOffset)
                    setAppliedStartOffset(defaultStartOffset)
                    setAppliedEndOffset(defaultEndOffset)
                    setAppliedTimeStart(windowStart.toISOString())
                    setAppliedTimeEnd(windowEnd.toISOString())
                    setWindowSizeSeconds(defaultWindowSize)
                }
            }
            else {
                setAppliedTimeStart(null)
                setAppliedTimeEnd(null)
                setAppliedStartOffset(0)
                setAppliedEndOffset(0)
                // ensure window size is not larger than session (session unknown -> keep default)
                setWindowSizeSeconds((ws) => Math.min(ws, Math.max(1, sessionTotalSeconds)))
            }
        }).catch(() => {}).finally(() => setLoading(false))
    }, [selectedProtocol, refreshTrigger, autoRefreshTick])

    // load RMS/SNR to extract canonical channel list
    useEffect(() => {
        if (!selectedProtocol) return
        setLoading(true)
        loadProtocolRMSSNRData(activeCompany.id, selectedProtocol).then((data) => {
            const rows = (data.data || []) as Array<[string, string, number, number]>;
            if (rows.length === 0) {
                setLoading(false)
                return
            }

            // extract canonical channel order
            const channelSet = new Set<string>();
            rows.forEach((r) => {
                const channel = r[1];
                channelSet.add(channel);
            });
            const canonicalOrder = Array.from(channelSet).sort();
            setChannelOrder(canonicalOrder);
            // set channel options for Select
            setChannelOptions(canonicalOrder.map((c) => ({value: c, label: c})));

        }).catch(() => {}).finally(() => setLoading(false))
    }, [selectedProtocol, refreshTrigger, autoRefreshTick])

    // fetch counts and populate channel options; prefer canonical channelOrder from RMSSNR and append any extra DB channels
    useEffect(() => {
        if (!selectedProtocol || appliedTimeStart=="unset" || appliedTimeEnd=="unset" || !channelOrder) return
        setLoading(true)
        // use appliedThreshold so counts only change when Update has been pressed
        loadProtocolSpikeCountsData(activeCompany.id, selectedProtocol, appliedThreshold, appliedTimeStart, appliedTimeEnd).then((data) => {
            // data.data expected to be array of [channel_name, count]
            // build counts map
            const counts = new Map<string, number>();
            const countsChannelSet = new Set<string>();
            (data.data || []).forEach(([channel, count]: [string, number]) => {
                countsChannelSet.add(channel);
                counts.set(channel, count);
            });
            setCountsMap(counts);
            // build channel options using canonical order + any extra channels from counts
            const extraChannels = Array.from(countsChannelSet).filter((ch) => !channelOrder.includes(ch));
            const fullChannelOrder = [...channelOrder, ...extraChannels].sort();
            setChannelOptions(fullChannelOrder.map((c) => ({value: c, label: c})));
            // set channel order state
            setChannelOrderToUse(fullChannelOrder);

            // If there is no input selection yet, set both input and applied channel to the first
            if (!selectedChannel && fullChannelOrder.length > 0) {
                setSelectedChannel(fullChannelOrder[0]);
                setAppliedChannel(fullChannelOrder[0]);
                setAppliedLimit(limit)
                setAppliedThreshold(thresholdMultiplier)
                // set total spikes for the initially applied channel
                setTotalSpikes(counts.get(fullChannelOrder[0]) || 0)
            } else {
                // If an applied channel exists, set total spikes to its count; otherwise 0
                if (appliedChannel && counts.has(appliedChannel)) {
                    setTotalSpikes(counts.get(appliedChannel) || 0)
                } else {
                    setTotalSpikes(0)
                }
            }

        }).catch(() => {
        }).finally(() => setLoading(false))
    }, [selectedProtocol, refreshTrigger, appliedThreshold, appliedTimeStart, appliedTimeEnd, channelOrder, autoRefreshTick])

    // Reset page when applied selection changes
    useEffect(() => {
        setPage(1)
    }, [appliedChannel, appliedLimit, appliedThreshold, appliedTimeStart, appliedTimeEnd])

    // fetch waveforms for selected channel and page/threshold
    useEffect(() => {
        if (!selectedProtocol || !appliedChannel) return;
        setLoading(true)
        const offset = (page - 1) * appliedLimit;
        loadProtocolSpikesData(activeCompany.id, selectedProtocol, appliedLimit, offset, appliedThreshold, appliedChannel, appliedTimeStart, appliedTimeEnd).then((data) => {
            // data.data contains [time_iso, channel_name, spike_amplitude, sample_frequency, waveform]
            // create traces for waveform scatter for the selected channel
            const spikes = data.data as Array<[string, string, number, number, Array<number>]>

            const waveformTemplate = (data.figure.data && (data.figure.data[1] as any)) || {};

            const waveformTraces: any[] = spikes.map((entry, i) => {
                const [time_iso, channel_name, spike_amplitude, sample_frequency, waveform] = entry;
                // Here spike time is time_iso; create x as index offsets converted to milliseconds using sample_frequency.
                const mid = Math.floor(waveform.length / 2);
                const sf = (sample_frequency && sample_frequency > 0) ? sample_frequency : 30000; // fallback to 30kHz if missing
                const x = waveform.map((_, idx) => ((idx - mid) * (1.0 / sf) * 1000.0)); // in ms
                return {
                    ...waveformTemplate,
                    x,
                    y: waveform,
                    mode: 'lines',
                    name: `${channel_name} ${i}`,
                    marker: {size: 4},
                    hovertemplate: `<b>${channel_name}:<b> %{x}, %{y}<extra></extra>`,
                    showlegend: false,
                    xaxis: waveformTemplate.xaxis || 'x2',
                    yaxis: waveformTemplate.yaxis || 'y2',
                };
            })

            const layout = {
                ...data.figure.layout,
            }
            // keep counts trace (if provided by server in data.figure.data[0]) and append waveforms in subplot 2
            const countTraceRaw = (data.figure.data && (data.figure.data[0] as any)) || null;
            const traces: any[] = [];

            if (countTraceRaw) {
                // build up-to-date channel order and counts using countsMap
                const countsY = channelOrderToUse.map((ch) => countsMap.get(ch) || 0);
                // determine base color from template if available
                const templateMarker = countTraceRaw.marker || {};
                const baseColor = templateMarker.color ?? templateMarker.fillcolor ?? 'rgba(31,119,180,0.8)';

                // build colors array where selected channel is green
                const colors = channelOrderToUse.map((ch) => (ch === appliedChannel ? 'green' : baseColor));

                const countTrace: any = {
                    ...countTraceRaw,
                    type: countTraceRaw.type || 'bar',
                    x: channelOrderToUse,
                    y: countsY,
                    marker: {
                        ...templateMarker,
                        color: colors,
                    },
                    hovertemplate: countTraceRaw.hovertemplate || '%{x}: %{y}<extra></extra>',
                };
                traces.push(countTrace);
            }

            // append waveform traces
            waveformTraces.forEach((t) => traces.push(t))

            // set total spikes for pagination to counts for the applied channel if available
            if (appliedChannel && countsMap.has(appliedChannel)) {
                setTotalSpikes(countsMap.get(appliedChannel) || 0)
            }
            else {
                setTotalSpikes(0)
            }

            setSpikesData({layout, config: {responsive: true}, data: traces as any})
        }).catch(() => {
        }).finally(() => setLoading(false))
    }, [selectedProtocol, appliedChannel, page, appliedLimit, appliedThreshold, appliedTimeStart, appliedTimeEnd, refreshTrigger, countsMap, autoRefreshTick])

    const renderLoading = () => {
        return (
        <Box style={{position: 'absolute', zIndex: '10', top: 'calc(50% - 2em)', left: 'calc(50% - 2em)'}}>
            <Loader size="4em" color="var(--bios-strong-accent)"/>
        </Box>
        )
    }

    const totalPages = Math.max(1, Math.ceil(totalSpikes / appliedLimit))
    const offset = (page - 1) * appliedLimit
    const displayedSpikeStart = Math.min(offset + 1, totalSpikes)
    const displayedSpikeEnd = Math.max(0, Math.min(offset + appliedLimit, totalSpikes))

    // true when session time info is not available (applied times null or session length unknown)
    const timeInfoMissing = appliedTimeStart == null || appliedTimeEnd == null || sessionTotalSeconds <= 0

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {/* Controls row - fixed height */}
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Channel</Text>
                    <Select
                        placeholder="Select channel"
                        data={channelOptions}
                        value={selectedChannel}
                        onChange={(v) => {setSelectedChannel(v)}}
                        style={{width: '12rem'}}
                    />
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Threshold (x RMS)</Text>
                    <NumberInput
                        value={thresholdMultiplier}
                        onChange={(v) => {
                            if (typeof v === 'number') setThresholdMultiplier(v)
                        }}
                        max={-4}
                        step={0.5}
                        placeholder="Threshold multiplier"
                        style={{width: '7rem'}}
                    />
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Time window (s)</Text>
                    <NumberInput
                        value={windowSizeSeconds}
                        disabled={timeInfoMissing}
                        onChange={(v) => {
                            if (typeof v !== 'number') return
                            const newSize = Math.max(1, Math.min(v, Math.max(1, sessionTotalSeconds)))
                            // adjust startOffset if window would overflow
                            const maxStart = Math.max(0, sessionTotalSeconds - newSize)
                            const newStart = Math.min(startOffset, maxStart)
                            setWindowSizeSeconds(newSize)
                            setStartOffset(newStart)
                            setEndOffset(newStart + newSize)
                        }}
                        min={1}
                        max={Math.max(1, sessionTotalSeconds)}
                        step={1}
                        style={{width: '7rem'}}
                    />
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Time offset (s)</Text>
                    <Box style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <Button size="xs" disabled={timeInfoMissing} onClick={() => {
                            if (sessionTotalSeconds <= 0) return
                            // move left by one window width, or to 0 if closer
                            const step = Math.max(1, windowSizeSeconds)
                            const newStart = Math.max(0, startOffset - step)
                            const clampedStart = Math.min(newStart, Math.max(0, sessionTotalSeconds - windowSizeSeconds))
                            setStartOffset(clampedStart)
                            setEndOffset(clampedStart + windowSizeSeconds)
                        }}>{'←'}</Button>

                        <RangeSlider
                            value={[startOffset, Math.min(startOffset + windowSizeSeconds, sessionTotalSeconds)]}
                            min={0}
                            max={sessionTotalSeconds}
                            step={0.001}
                            disabled={timeInfoMissing}
                            // only allow moving the whole window: interpret any change by setting startOffset to the lower value
                            onChange={(pair: [number, number]) => {
                                if (timeInfoMissing) return
                                const low = Math.min(pair[0], pair[1])
                                // clamp low so window fits
                                const clampedLow = Math.min(Math.max(0, low), Math.max(0, sessionTotalSeconds - windowSizeSeconds))
                                setStartOffset(clampedLow)
                                setEndOffset(clampedLow + windowSizeSeconds)
                            }}
                            style={{width: '12rem'}}
                        />

                        <Button size="xs" disabled={timeInfoMissing} onClick={() => {
                            if (sessionTotalSeconds <= 0) return
                            const step = Math.max(1, windowSizeSeconds)
                            const maxStart = Math.max(0, sessionTotalSeconds - windowSizeSeconds)
                            const newStart = Math.min(maxStart, startOffset + step)
                            setStartOffset(newStart)
                            setEndOffset(newStart + windowSizeSeconds)
                        }}>{'→'}</Button>

                        <Text size="sm">{Math.round(startOffset)} s - {Math.round(endOffset)} s</Text>
                    </Box>
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Displayed Spikes</Text>
                    <Box style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                        <NumberInput
                            value={limit}
                            onChange={(v) => {
                                if (typeof v === 'number') {
                                    const newLimit = Math.max(1, Math.min(Math.floor(v), totalSpikes || 1));
                                    setLimit(newLimit)
                                }
                            }}
                            min={1}
                            max={Math.max(1, totalSpikes)}
                            step={1}
                            placeholder="Limit"
                            style={{width: '7rem'}}
                        />
                        <Button size="xs" onClick={() => {
                        // commit channel/limit/threshold and time offsets
                        setAppliedChannel(selectedChannel);
                        setAppliedLimit(limit);
                        setAppliedThreshold(thresholdMultiplier);
                        setAppliedStartOffset(startOffset);
                        setAppliedEndOffset(endOffset);
                        // compute appliedTimeStart and appliedTimeEnd from session start + offsets (seconds)
                        if (sessionStart) {
                            const ss = new Date(sessionStart).getTime();
                            const ats = ss + startOffset * 1000;
                            const ate = ss + endOffset * 1000;
                            setAppliedTimeStart(isoFromMs(ats));
                            setAppliedTimeEnd(isoFromMs(ate));
                        } else {
                            setAppliedTimeStart(null);
                            setAppliedTimeEnd(null);
                        }
                    }} disabled={appliedChannel === selectedChannel && appliedLimit === limit && appliedThreshold === thresholdMultiplier && appliedStartOffset === startOffset && appliedEndOffset === endOffset}>Update</Button>
                    </Box>
                </Box>

                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem', justifyContent: 'center'}}>
                    <Text size="xs" color="dimmed">Page</Text>
                    <Box style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                        <Button size="xs" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>Prev</Button>
                        <Text size="sm">Showing {displayedSpikeStart} - {displayedSpikeEnd} of {totalSpikes} spikes</Text>
                        <Button size="xs" disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))}>Next</Button>
                     </Box>
                 </Box>
            </Box>

            {/* Plot area - fills remaining space */}
            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {!selectedChannel ? (
                    <Box style={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Text>Please select a channel</Text>
                    </Box>
                ) : (
                    <Box style={{height: '100%', width: '100%', minHeight: 0, position: 'relative'}}>
                        {/* Dismissable warning overlay when time info is missing (does not block plotting) */}
                        {(!timeWarningDismissed && (appliedTimeStart == null || appliedTimeEnd == null)) && (
                            <Box style={{position: 'absolute', zIndex: 20, left: '0.5rem', right: '0.5rem', top: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,245,224,0.95)', border: '0.07rem solid #f5c26b', borderRadius: '0.25rem'}}>
                                <Text size="sm" color="dark">Time information missing</Text>
                                <Button size="xs" variant="subtle" onClick={() => setTimeWarningDismissed(true)}>Dismiss</Button>
                            </Box>
                        )}
                         {spikesData ? (
                             <Box style={{height: '100%', width: '100%'}}>
                                 <PlotlyPlot {...spikesData} />
                             </Box>
                         ) : renderLoading()}
                         {loading ? renderLoading() : null}
                     </Box>
                 )}
             </Box>
        </Box>
    )
}
